import { compose } from "infrastructure/store";
import { delay } from "util/support";
import { ECommerceOnboardingApplication, supportedPaymentMethods, SupportedPaymentMethodsTag } from "consts/onboarding";
import { useSimpleRequest } from "hooks";
import type { OnboardingPaymentMethod } from "features/onboarding/types";
import { ECommerce } from "ui/forms/Onboarding";
import {
    useRequestedChanges,
    useFieldsStatuses,
    useFieldsComments,
    useApplicationDetailContext,
    useStepStatus,
    useApplicationStatus
} from "ui/organizms/Onboarding/Applications/common";
import useCountriesAvailable from "./useCountriesAvailable";
import { putECommercePaymentMethodsSteps } from "features/onboarding/api";
import { FormAdapter } from "hooks/useForm";

export const usePaymentMethods = () => {
    const { getTotalStepFields, getStepData, fetchStep } = useApplicationDetailContext();

    const totalStepFields = getTotalStepFields(ECommerceOnboardingApplication.PaymentMethods);
    const data = getStepData<OnboardingPaymentMethod>(ECommerceOnboardingApplication.PaymentMethods);

    const {
        getStepStatus
    } = useStepStatus();

    const {
        updateApplicationStatus
    } = useApplicationStatus();

    const {
        withRequestedChangeFields,
        serializeRequestedChangeFields
    } = useRequestedChanges();

    const {
        withFieldsStatuses,
        serializeFieldsStatuses
    } = useFieldsStatuses();

    const {
        withFieldsComments,
        serializeFieldsComments
    } = useFieldsComments();

    const request = useSimpleRequest();

    const stepDataWithRequestedChangeFields = compose<OnboardingPaymentMethod>(
        withRequestedChangeFields,
        withFieldsStatuses,
        withFieldsComments
    )(data);

    const { getCountriesAvailable } = useCountriesAvailable([
        SupportedPaymentMethodsTag.Bancontact,
        SupportedPaymentMethodsTag.Blik,
        SupportedPaymentMethodsTag.Eps,
        SupportedPaymentMethodsTag.Giropay,
        SupportedPaymentMethodsTag.Ideal,
        SupportedPaymentMethodsTag.Mbway,
        SupportedPaymentMethodsTag.Klarna,
        SupportedPaymentMethodsTag.P24
    ]);

    const paymentMethods = new Map<SupportedPaymentMethodsTag, ECommerce.PaymentMethods.PaymentMethod>()
        .set(SupportedPaymentMethodsTag.Bancontact, {
            ...supportedPaymentMethods[SupportedPaymentMethodsTag.Bancontact],
            countriesAvailable: getCountriesAvailable(SupportedPaymentMethodsTag.Bancontact)
        })
        .set(SupportedPaymentMethodsTag.Blik, {
            ...supportedPaymentMethods[SupportedPaymentMethodsTag.Blik],
            countriesAvailable: getCountriesAvailable(SupportedPaymentMethodsTag.Blik)
        })
        .set(SupportedPaymentMethodsTag.Eps, {
            ...supportedPaymentMethods[SupportedPaymentMethodsTag.Eps],
            countriesAvailable: getCountriesAvailable(SupportedPaymentMethodsTag.Eps)
        })
        .set(SupportedPaymentMethodsTag.Giropay, {
            ...supportedPaymentMethods[SupportedPaymentMethodsTag.Giropay],
            countriesAvailable: getCountriesAvailable(SupportedPaymentMethodsTag.Giropay)
        })
        .set(SupportedPaymentMethodsTag.Ideal, {
            ...supportedPaymentMethods[SupportedPaymentMethodsTag.Ideal],
            countriesAvailable: getCountriesAvailable(SupportedPaymentMethodsTag.Ideal)
        })
        .set(SupportedPaymentMethodsTag.Mbway, {
            ...supportedPaymentMethods[SupportedPaymentMethodsTag.Mbway],
            countriesAvailable: getCountriesAvailable(SupportedPaymentMethodsTag.Mbway)
        })
        .set(SupportedPaymentMethodsTag.Klarna, {
            ...supportedPaymentMethods[SupportedPaymentMethodsTag.Klarna],
            countriesAvailable: getCountriesAvailable(SupportedPaymentMethodsTag.Klarna)
        })
        .set(SupportedPaymentMethodsTag.P24, {
            ...supportedPaymentMethods[SupportedPaymentMethodsTag.P24],
            countriesAvailable: getCountriesAvailable(SupportedPaymentMethodsTag.P24)
        });

    const getPaymentMethods = () => Array.from(paymentMethods.values());

    const formProps: ECommerce.PaymentMethods.OnboardingPaymentMethodsForm = {
        isBootstrapDataLoading: false,
        bootstrapData: {
            methods: getPaymentMethods()
        },
        initialValues: ECommerce.PaymentMethods.getInitialValues({
            ...stepDataWithRequestedChangeFields,
            methods: data.methods
                ?.split(',')
                .filter(tag => paymentMethods.has(tag as SupportedPaymentMethodsTag))
        }),
        onSaveOrCreate: async ({
            methods = [],
            ...restFormValues
        }, helpers) => {
            const response = await request(() => putECommercePaymentMethodsSteps({
                userId: data.userId,
                methods: Array.from(new Set(methods))
                    .join(','),
                requestedChangeFields: serializeRequestedChangeFields(restFormValues),
                fieldsStatuses: serializeFieldsStatuses(restFormValues),
                fieldsComments: serializeFieldsComments(restFormValues),
                status: getStepStatus(restFormValues, totalStepFields)
            }));

            helpers!.setTouched({});

            await delay(1000)
                .then(() => fetchStep(ECommerceOnboardingApplication.PaymentMethods))
                .then(() => delay(1000))
                .then(updateApplicationStatus);

            return FormAdapter.fromResponse(response);
        }
    };

    return {
        ...formProps,
        ...ECommerce.PaymentMethods.useForm(formProps),
        ...stepDataWithRequestedChangeFields
    };
};

export default usePaymentMethods;
